import Papa from 'papaparse';
import moment from 'moment';
import { isString } from 'lodash-es';
import type { CoreTypes } from '../../../core';
import type { BaseOptions } from '../../../common/types';
import { getVertexId } from '../../../core/util';
import { isMongooseObjectId } from '../../../common/functions';

const papaOptions = {
  quotes:         false, //or array of booleans
  quoteChar:      '"',
  escapeChar:     '"',
  delimiter:      '\t',
  header:         true,
  newline:        '\r\n',
  skipEmptyLines: false, //other option is 'greedy', meaning skip delimiters, quotes, and whitespace.
  // columns: null //or array of strings
} as Papa.UnparseConfig;

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export default (
  data:    CoreTypes.Mixed.Schedule,
  options: BaseOptions = {}
): string => {
  const { locations, teachers, groups, persons, events, courses, periods, syllabuses, divisions } = data;
  const { start, /* end, */ displayName } = divisions ?? data as unknown as CoreTypes.Mixed.Division;

  const startYear  = moment.utc(start).year();
  const startMonth = moment.utc(start).month();

  // Set cvs to the source section
  let csv = `[Source]${papaOptions.newline}ApplicationVerMajor=4${papaOptions.newline}ApplicationVerMinor=0${papaOptions.newline}DatabaseRevNo=527${papaOptions.newline}CurrentNovaschemDatabase=${papaOptions.newline}User=${papaOptions.newline}`;
  csv += `SchoolID=RS${papaOptions.newline}School=${displayName}${papaOptions.newline}County=RS${papaOptions.newline}`;
  csv += `BaseYear=${startYear}${papaOptions.newline}OffsetYear=0${papaOptions.newline}OffsetMonth=${startMonth}${papaOptions.newline}CreationDate=${moment().format('YYYY-MM-DD')}${papaOptions.newline}${papaOptions.newline}`;
  // add destination section to csv
  csv += `[Destination]${papaOptions.newline}Application=${papaOptions.newline}ApplicationID=${papaOptions.newline}${papaOptions.newline}`;

  // add database options to csv
  csv += `[DatabaseOptions]${papaOptions.newline}Update=TRUE${papaOptions.newline}AddNew=TRUE${papaOptions.newline}${papaOptions.newline}`;

  // start tables section
  csv += `[Tables]${papaOptions.newline}${papaOptions.newline}`;

  // append teachers to csv
  const t = teachers?.map(teacher => {
    return {
      'Teacher (6001)':          teacher.signature,
      'GUID (5800)':             (teacher.ids && uuid.test(teacher.ids)) ? `{${teacher.ids}}` : '',
      'Category (6002)':         '',
      'Title (6006)':            '',
      'Phone (6033)':            '',
      'EvaluationNumber (6014)': '',
      ... 'person' in teacher && teacher.person && typeof teacher.person == 'object'
        ? {
          'PersonalCode (6028)': 'SSN'       in teacher.person ? (teacher.person?.SSN?.value         ?? '') : '',
          'LastName (6003)':     'lastName'  in teacher.person ? (teacher.person?.lastName           ?? '') : '',
          'FirstName (6004)':    'firstName' in teacher.person ? (teacher.person?.firstName          ?? '') : '',
          'EMail (6034)':        'emails'    in teacher.person ? (teacher.person?.emails?.[0]?.value ?? '') : '',
        }
        : null
    };
  }) ?? [];
  csv += `${papaOptions.newline}Teacher (6000)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (t.length) {
    csv += Papa.unparse(t, papaOptions);
  } else {
    csv += ['Teacher (6001)', 'GUID (5800)', 'Category (6002)', 'PersonalCode (6028)', 'LastName (6003)', 'Title (6006)', 'FirstName (6004)', 'Phone (6033)', 'EMail (6034)', 'EvaluationNumber (6014)'].join(papaOptions.delimiter);
  }
  csv += papaOptions.newline;

  // append students to csv
  const s = persons?.filter(person => {
    return person.type != 'Teacher';
  }).map(person => {
    return {
      'Student (7201)':   person.SSN?.value,
      'GUID (5800)':      (person.ids && uuid.test(person.ids)) ? `{${person.ids}}` : '',
      'Category (7305)':  '',
      'LastName (7202)':  person.lastName ?? '',
      'FirstName (7211)': person.firstName ?? '',
      'EMail (7215)':     person.emails?.[0]?.value ?? ''
    };
  }) ?? [];
  csv += `${papaOptions.newline}Student (7200)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (s.length) {
    csv += Papa.unparse(s, papaOptions);
  } else {
    csv += ['Student (7201)', 'GUID (5800)', 'Category (7305)', 'LastName (7202)', 'FirstName (7211)', 'EMail (7215)'].join(papaOptions.delimiter);
  }
  csv += papaOptions.newline;

  // create a map of students.id to students.SSN
  const studentMap = new Map(persons?.filter(x => x.type != 'Teacher').map(x => [getVertexId(x, options), x.SSN?.value ?? '']));

  // append groups to csv
  const g = groups?.map(group => {
    return {
      'Group (6201)':   group.displayName!,
      'GUID (5800)':    (group.ids && uuid.test(group.ids)) ? `{${group.ids}}` : '',
      'Class (6210)':   group.species == 'class' ? 1 : 0,
      'Teacher (6204)': '',
      'IClass (6206)':  '',
      'Student (6205)': group.members?.map(x => getVertexId(x, options)).map(x => studentMap.get(x)).filter(Boolean).join(',')
    };
  }) ?? [];
  csv += `${papaOptions.newline}Group (6200)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (g.length) {
    csv += Papa.unparse(g, papaOptions);
  } else {
    csv += ['Group (6201)', 'GUID (5800)', 'Class (6210)', 'Teacher (6204)', 'IClass (6206)', 'Student (6205)'].join(papaOptions.delimiter);
  }
  csv += papaOptions.newline;

  // append locations to csv
  const l = locations?.map(location => {
    return {
      'Room (6601)':            location.displayName!,
      'GUID (5800)':            (location.ids && uuid.test(location.ids)) ? `{${location.ids}}` : '',
      'Category (6602)':        location.tags?.find(tag => tag.type == 'category')?.value ?? '',
      'SeatingCapacity (6607)': location.maximumCapacity,
    };
  }) ?? [];
  csv += `${papaOptions.newline}Room (6600)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (l.length) {
    csv += Papa.unparse(l, papaOptions);
  } else {
    csv += ['Room (6601)', 'GUID (5800)', 'Category (6602)', 'SeatingCapacity (6607)'].join(papaOptions.delimiter);
  }
  csv += papaOptions.newline;

  // append periods to csv
  const p = periods?.map(period => {
    return {
      'Period (8001)':            period.displayName!,
      'GUID (5800)':              (period.ids && uuid.test(period.ids)) ? `{${period.ids}}` : '',
      'TextBelowSchedule (8002)': period.displayName!,
      'Category (8006)':          '',
      'Calendar (8005)':          period.ranges?.map(range => `${moment.utc(range.start).format('D/M')}-${moment.utc(range.end).format('D/M')}`).join(', ')
    };
  }) ?? [];
  csv += `${papaOptions.newline}Period (8000)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (p.length) {
    csv += Papa.unparse(p, papaOptions);
  } else {
    csv += ['Period (8001)', 'GUID (5800)', 'TextBelowSchedule (8002)', 'Category (8006)', 'Calendar (8005)'].join(papaOptions.delimiter);
  }
  csv += papaOptions.newline;

  const courseMap   = new Map(data.courses   ?.map(x => [getVertexId(x, options), x]));
  const periodMap   = new Map(data.periods   ?.map(x => [getVertexId(x, options), x]));
  const teacherMap  = new Map(data.teachers  ?.map(x => [getVertexId(x, options), x]));
  const groupMap    = new Map(data.groups    ?.map(x => [getVertexId(x, options), x]));
  const locationMap = new Map(data.locations ?.map(x => [getVertexId(x, options), x]));
  const syllabusMap = new Map(data.syllabuses?.map(x => [getVertexId(x, options), x]));

  // append lessons to csv
  const e = events
    // ?.filter(x => !isNaN(Date.parse(x.start?.toString() ?? '')))
    .map((event, index) => {
      if (!event.start || !event.end) return;

      const courseId = event.course ? getVertexId(event.course, options) : undefined;
      const course = courseId ? courseMap.get(courseId) : undefined;

      // Only use deserialize period
      let period = event.period == null ? course?.period : event.period;
      if (isString(period))
        period = periodMap.get(period);
      if ((period && !('ranges' in period)))
        period = undefined;

      // Only use deserialize teachers
      const t          = event.teachers == null ? course?.teachers : event.teachers;
      const teacherIds = t?.map(x => getVertexId(x.to, options));
      const teachers   = teacherIds?.map(id => teacherMap.get(id)).filter(Boolean) ?? [];

      // Only use deserialize groups
      const g          = event.groups == null ? course?.groups : event.groups;
      const groupIds   = g?.map(x => getVertexId(x.to, options));
      const groups     = groupIds?.map(id => groupMap.get(id)).filter(Boolean) ?? [];

      // Only use deserialize locations
      const locationIds = event.inLocations?.map(x => x ? getVertexId(x, options) : null).filter(x => x != null);
      const locations   = locationIds?.map(id => locationMap.get(id)).filter(Boolean) ?? [];
      const dayOfWeek   = moment.utc(event.start).toDate().toLocaleDateString('sv-SE', { weekday: 'long', timeZone: 'UTC' });

      // Syllabus of the course
      const syllabusId = isString(course?.syllabus)
        ? course.syllabus
        : (course?.syllabus && isMongooseObjectId(course.syllabus))
          ? course.syllabus.toString()
          : undefined;
      const syllabus = syllabusId ? syllabusMap.get(syllabusId) : course?.syllabus as CoreTypes.Deep.Syllabus | undefined;

      // capitalize first letter of dayOfWeek
      const dayOfWeekCapitalized = dayOfWeek.charAt(0).toUpperCase() + dayOfWeek.slice(1);
      return {
        'PK (7100)':          index + 1,
        'GUID (5800)':        (event.ids && uuid.test(event.ids)) ? `{${event.ids}}` : '',
        'DayOfWeek (7101)':   dayOfWeekCapitalized,
        'Starttime (7102)':   moment.utc(event.start).format('HH:mm'),
        'Length (7115)':      event.duration,
        'Course (7106)':      syllabus?.courseCode,
        'Subject (7103)':     syllabus?.subjectCode ?? course?.subject ?? '',
        'Teacher (7104)':     teachers ?.map(x => x?.signature  ).filter(Boolean).join(','),
        'Group (7105)':       groups   ?.map(x => x?.displayName).filter(Boolean).join(','),
        'Room (7107)':        locations?.map(x => x?.displayName).filter(Boolean).join(','),
        'Period (7108)':      '',
        'Week (7109)':        '',
        'ActualWeeks (7167)': period?.ranges?.map(range => `${moment.utc(range.start).week()}-${moment.utc(range.end).week()}`).join(',')
      };
    }).filter(Boolean) ?? [];
  csv += `${papaOptions.newline}Lesson (7100)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (e.length) {
    csv += Papa.unparse(e, papaOptions);
  } else {
    csv += ['PK (7100)', 'GUID (5800)', 'DayOfWeek (7101)', 'Starttime (7102)', 'Length (7115)', 'Course (7106)', 'Subject (7103)', 'Period (7108)', 'Week (7109)', 'ActualWeeks (7167)'].join(papaOptions.delimiter);
  }
  csv += papaOptions.newline;

  // append subjects to csv, is empty for now
  // csv += `Subject (6400)${options.newline}[Rows]${options.newline}`;
  // csv += ['Subject (6401)', 'GUID (5800)', 'Category (6402)', 'FullText (6403)', 'BackgroundColor (6432)', 'TextColor (6433)', 'EvaluationNumber (6429)'].join(options.delimiter);
  // csv += options.newline;

  // append syllabus to csv
  const courseSyllabuses = syllabuses?.filter(syllabus => !!syllabus.courseName).map(syllabus => {
    return {
      'Course (7601)':   syllabus.courseCode,
      'GUID (5800)':     syllabus.ids,
      'FullText (7602)': syllabus.courseName,
      'Subject (7603)':  syllabus.subjectCode ?? '',
      'Points (7604)':   syllabus.points
    };
  }) ?? [];
  csv += `${papaOptions.newline}Course (7600)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (courseSyllabuses.length) {
    csv += Papa.unparse(courseSyllabuses, papaOptions);
  } else {
    csv += ['PK (7100)', 'GUID (5800)', 'DayOfWeek (7101)', 'Starttime (7102)', 'Length (7115)', 'Course (7106)', 'Subject (7103)', 'Period (7108)', 'Week (7109)', 'ActualWeeks (7167)'].join(papaOptions.delimiter);
  }
  csv += papaOptions.newline;

  const subjectSyllabuses = syllabuses?.reduce((unique, syllabus) => {
    if (syllabus.subjectCode && !unique.seen.has(syllabus.subjectCode)) {
      unique.seen.add(syllabus.subjectCode);
      unique.result.push({
        'Subject (6401)':  syllabus.subjectCode,
        'GUID (5800)':     syllabus.ids,
        'FullText (6403)': syllabus.subjectName,
      });
    }
    return unique;
  }, {
    seen:   new Set<string>(),
    result: [] as Array<{
      'Subject (6401)':  string;
      'GUID (5800)':     string | undefined;
      'FullText (6403)': string;
    }>
  })?.result ?? [];
  csv += `Subject (6400)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (subjectSyllabuses.length) {
    csv += Papa.unparse(subjectSyllabuses, papaOptions);
  } else {
    csv += ['Subject (6401)', 'GUID (5800)', 'Category (6402)', 'FullText (6403)', 'BackgroundColor (6432)', 'TextColor (6433)', 'EvaluationNumber (6429)'].join(papaOptions.delimiter);
  }

  const totalHoursRegex = /(\d+) hrs/;
  const weekTimeRegex = /(\d+) min\/week/;
  const c = courses?.map(course => {
    // Only use deserialize teachers
    const teacherIds = course.teachers?.map(x => getVertexId(x.to, options)).filter(Boolean);
    const teachers   = teacherIds?.map(id => teacherMap.get(id)!).filter(Boolean) ?? [];

    // Only use deserialize groups
    const groupIds   = course.groups?.map(x => getVertexId(x.to, options)).filter(Boolean);
    const groups     = groupIds?.map(id => groupMap.get(id)!).filter(Boolean) ?? [];

    // Only use deserialize syllabus
    const syllabusId = isString(course?.syllabus)
      ? course.syllabus
      : (course?.syllabus && isMongooseObjectId(course.syllabus))
        ? course.syllabus.toString()
        : undefined;
    const syllabus = syllabusId ? syllabusMap.get(syllabusId) : course?.syllabus as CoreTypes.Deep.Syllabus | undefined;

    return {
      'PK (8200)':             '',
      'GUID (5800)':           course.ids,
      'OriginalRecord (8224)': '1',
      'CreateLesson (8227)':   '1',
      'Course (8202)':         syllabus?.courseCode,
      'Subject (8209)':        syllabus?.subjectCode ?? course.subject, // Nikos ansvarig för framtida buggar
      'Teacher (8210)':        teachers?.map(teacher => teacher.signature).filter(Boolean).join(','),
      'Group (8203)':          groups?.map(group => group.displayName).filter(Boolean).join(','),
      'TotalHours (8204)':     (course.plannedDuration && totalHoursRegex.test(course.plannedDuration)) ? totalHoursRegex.exec(course.plannedDuration)![1] : '',
      'Weektime (8208)':       (course.plannedDuration && weekTimeRegex.test(course.plannedDuration)) ? weekTimeRegex.exec(course.plannedDuration)![1] : '',
      'Blockname (8215)':      '',
      'Period (8206)':         '',
      'Week (8207)':           '',
      'NotPeriod (8212)':      '',
      'NotWeek (8213)':        ''
    };
  }) ?? [];
  csv += `${papaOptions.newline}TA (8200)${papaOptions.newline}[Rows]${papaOptions.newline}`;
  if (c.length) {
    csv += Papa.unparse(c, papaOptions);
  } else {
    csv += ['Course (8202)', 'GUID (5800)', 'Subject (8209)', 'Teacher (8210)', 'Group (8203)', 'TotalHours (8204)', 'Weektime (8208)', 'Blockname (8215)', 'Period (8206)', 'Week (8207)', 'NotPeriod (8212)', 'NotWeek (8213)'].join(papaOptions.delimiter);
  }

  return csv;
};